// This is the base chart used for all themes. Spark/Polaris/Wix wrap this with their styling
import { Badge, Card, Text } from '@ui/themed';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ChartDataPoint, ChartProps } from './types';

type TooltipPayload = {
  value: number;
  name: string;
  color: string;
  dataKey: string;
  payload: ChartDataPoint;
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: TooltipPayload[];
  title: string;
  valueFormatter: (value: number) => string;
  xAxisFormatter: (value: Date) => string;
};

type LineChartProps = ChartProps & {
  style: {
    fill?: boolean;
    strokeWidth: number;
    primary: {
      col: string;
      dashWidth?: number;
    };
    secondary?: {
      col: string;
      dashWidth?: number;
    };
  };
};

function CustomTooltip({
  active,
  payload,
  title,
  valueFormatter,
  xAxisFormatter,
}: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  return (
    <Card>
      <div className="font-semibold">{title}</div>

      <div className="flex flex-col gap-3">
        {payload.map((entry) => {
          const isPrimary = entry.dataKey === 'primary.y';
          const rawX = isPrimary ? entry.payload.primary?.x : entry.payload.secondary?.x;

          const formattedX = rawX !== undefined ? xAxisFormatter(rawX) : 'Unknown';

          return (
            <div key={entry.name} className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <div className="size-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                <Text variant="secondary">{formattedX}</Text>
              </div>

              <div className="w-min">
                <Badge>{valueFormatter(Number(entry.value))}</Badge>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

const AnimatedActiveDot = ({
  cx,
  cy,
  stroke,
  fill,
  r,
}: {
  cx?: number;
  cy?: number;
  stroke: string;
  fill: string;
  r: number;
}) => {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={fill}
      stroke={stroke}
      strokeWidth={2}
      className="transition-all duration-150 ease-out"
    />
  );
};

export function LineChart({
  data,
  tooltipTitle,
  valueFormatter,
  xAxisFormatter,
  primaryLabel,
  secondaryLabel,
  style,
}: LineChartProps) {
  return (
    <div className="m-auto aspect-[1.5] w-full text-xs sm:aspect-[3] lg:aspect-[4]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="var(--color-spark-border-subtle)" />

          <XAxis
            dataKey="primary.x"
            tick={{ fill: 'var(--color-spark-text-secondary)' }}
            axisLine={false}
            tickLine={false}
            dy={10}
            minTickGap={30}
            tickFormatter={xAxisFormatter}
          />

          <YAxis
            width={70}
            tick={{ fill: 'var(--color-spark-text-secondary)' }}
            axisLine={false}
            tickLine={false}
            minTickGap={20}
            tickFormatter={(value) => valueFormatter(value, 'compact')}
          />

          <Tooltip
            content={
              <CustomTooltip
                title={tooltipTitle}
                valueFormatter={valueFormatter}
                xAxisFormatter={xAxisFormatter}
              />
            }
            cursor={{ stroke: 'var(--color-spark-border-default)', strokeWidth: 1.5 }}
          />

          {secondaryLabel && style.secondary && (
            <Area
              name={secondaryLabel}
              type="bump"
              dataKey="secondary.y"
              stroke={style.secondary.col}
              strokeWidth={style.strokeWidth}
              strokeDasharray={
                style.secondary.dashWidth
                  ? `${style.secondary.dashWidth} ${style.secondary.dashWidth}`
                  : undefined
              }
              dot={false}
              activeDot={
                <AnimatedActiveDot
                  r={4}
                  fill={style.secondary.col}
                  stroke="var(--color-spark-surface)"
                />
              }
              fill={style.secondary.col}
              fillOpacity={style.fill ? 0.2 : 0}
            />
          )}

          <Area
            name={primaryLabel}
            type="bump"
            dataKey="primary.y"
            stroke={style.primary.col}
            strokeWidth={style.strokeWidth}
            dot={false}
            strokeDasharray={
              style.primary.dashWidth
                ? `${style.primary.dashWidth} ${style.primary.dashWidth}`
                : undefined
            }
            activeDot={
              <AnimatedActiveDot
                r={5}
                fill={style.primary.col}
                stroke="var(--color-spark-surface)"
              />
            }
            fill={style.primary.col}
            fillOpacity={style.fill ? 0.2 : 0}
          />

          <Legend
            verticalAlign="bottom"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ paddingTop: '32px' }}
            formatter={(value: string) => (
              <span className="ps-0.5 pe-4 text-spark-text-secondary text-xs">{value}</span>
            )}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
